import React from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import * as R from 'ramda';

import { formatNumberWithoutDecimals } from 'utils/helpers/format';
import DataDisplayItem from '../../components/DataDisplayItem';
import { useViewBatchQuery } from '../../batchesEndpoints';

const infoContainerStyle = {
  backgroundColor: 'surface.light',
  columnGap: 1,
  display: 'grid',
  gridTemplateAreas: {
    lg: `"batchId"
        "origin"
        "location"
        "availableDate"
        "product"
        "volume"
    `,
    xs: `"batchId availableDate"
        "origin product"
        "location volume"`,
  },
  gridTemplateColumns: { md: '1fr', xs: '1fr 1fr' },
  rowGap: 2,
};

const ParentBatchDetailsContent = ({ totalProposedVolume }) => {
  const { batchId } = useParams();

  const { data: batchData = { batches: [] }, isFetching: isFetchingBatches } = useViewBatchQuery({
    params: { batchId },
  });

  const batchDataProp = R.prop(R.__, batchData);

  return (
    <>
      {isFetchingBatches ? (
        <Box textAlign="center" width="100%">
          <CircularProgress size={48} />
        </Box>
      ) : (
        <Box sx={infoContainerStyle}>
          <DataDisplayItem
            additionalStackStyling={{ gridArea: 'batchId' }}
            label="Batch ID"
            value={batchDataProp('batchId')}
            valueVariant="subtitle2"
          />
          <DataDisplayItem
            additionalStackStyling={{ gridArea: 'origin' }}
            label="Origin"
            value={batchDataProp('originName')}
            valueVariant="subtitle2"
          />
          <DataDisplayItem
            additionalStackStyling={{ gridArea: 'location' }}
            label="Default Location"
            value={batchDataProp('defaultLocName')}
            valueVariant="subtitle2"
          />
          <DataDisplayItem
            additionalStackStyling={{ gridArea: 'availableDate' }}
            label="Available Date"
            value={format(new Date(batchDataProp('availableDate')), 'P')}
            valueVariant="subtitle2"
          />
          <DataDisplayItem
            additionalStackStyling={{ gridArea: 'product' }}
            label="Product"
            value={batchDataProp('productName')}
            valueVariant="subtitle2"
          />
          <Stack spacing={0.5} sx={{ gridArea: 'volume' }}>
            <Stack alignItems="center" direction="row" spacing={1}>
              <Typography variant="caption">Volume</Typography>
              <Tooltip
                enterTouchDelay={10}
                title="Total Batch Volume equals the sum of all associated shipment volumes"
              >
                <InfoRoundedIcon sx={{ color: 'graphic.main', fontSize: '14px' }} />
              </Tooltip>
            </Stack>
            <Typography variant="subtitle2">
              {`${formatNumberWithoutDecimals(totalProposedVolume)} bbl`}
            </Typography>
          </Stack>
        </Box>
      )}
    </>
  );
};

ParentBatchDetailsContent.propTypes = {
  totalProposedVolume: PropTypes.number.isRequired,
};

export default ParentBatchDetailsContent;
